/**
 * PropertyDetailPage — /realestate/:propId  (id or slug)
 * Public detail with photo gallery, features, favorite toggle, contact info.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { propLabel, propGroupFor } from "@/data/propertyCategories";
import { ReportButton } from "@/pages/JobsDashboardPage";
import {
  ArrowLeft, MapPin, Home, Bed, Bath, Ruler, Car, Loader2, Mail, Phone, Heart, HeartOff,
  Share2, Building2,
} from "lucide-react";

function fmtPrice(price, currency = "USD", period) {
  if (price == null) return "Contact for price";
  try {
    const s = new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(price);
    return period ? `${s}/${period.replace("per-","")}` : s;
  } catch { return `${currency} ${price}`; }
}

export default function PropertyDetailPage() {
  const { propId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroIdx, setHeroIdx] = useState(0);
  const [favBusy, setFavBusy] = useState(false);
  const [favorited, setFavorited] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/properties/${propId}`);
      setP(data);
      if (user) {
        try {
          const { data: favs } = await http.get("/me/favorite-properties");
          setFavorited((favs.items || []).some((x) => x.id === data.id));
        } catch (_) { /* noop */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Property not found.");
      nav("/realestate");
    } finally { setLoading(false); }
  }, [propId, user, nav]);
  useEffect(() => { load(); }, [load]);

  const toggleFav = async () => {
    if (!user) { toast.error("Sign in to save properties."); return; }
    setFavBusy(true);
    try {
      const { data } = await http.post(`/properties/${p.id}/favorite`);
      setFavorited(data.favorited);
      toast.success(data.favorited ? "Saved to favorites." : "Removed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setFavBusy(false); }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: p.title, url }); }
      else { await navigator.clipboard.writeText(url); toast.success("Link copied!"); }
    } catch (_) { /* user cancelled */ }
  };

  if (loading) return <div className="max-w-3xl mx-auto py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" /></div>;
  if (!p) return null;

  const g = propGroupFor(p.category);
  const isOwner = user && p.posted_by === user.id;
  const photos = p.photos?.length ? p.photos : [];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="property-detail-page">
      <Link to="/realestate" className="text-xs opacity-70 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> All listings
      </Link>

      {/* Gallery */}
      <div className="rounded-xl overflow-hidden mb-4 grid gap-1 grid-cols-1 sm:grid-cols-4" style={{ aspectRatio: "16 / 9" }}>
        {photos[0] ? (
          <div className="sm:col-span-3 sm:row-span-2 bg-black/5 overflow-hidden">
            <img src={photos[heroIdx] || photos[0]} alt="" className="w-full h-full object-cover" data-testid="property-hero" />
          </div>
        ) : (
          <div className="sm:col-span-4 sm:row-span-2 bg-black/5 grid place-items-center text-8xl opacity-30">{g?.emoji || "🏡"}</div>
        )}
        {photos.slice(1, 5).map((src, i) => (
          <button key={i} onClick={() => setHeroIdx(i + 1)} className="bg-black/5 overflow-hidden aspect-square" data-testid={`property-thumb-${i}`}>
            <img src={src} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <main>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-lg">{g?.emoji}</span>
            <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{propLabel(p.category)}</span>
            <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ background: p.sale_or_rent === "sale" ? "#0E7C86" : "#B25D38" }}>
              For {p.sale_or_rent}
            </span>
          </div>
          <h1 className="font-display text-4xl mb-2" data-testid="property-title">{p.title}</h1>
          <p className="text-sm inline-flex items-center gap-1 opacity-80 mb-3">
            <MapPin className="w-4 h-4" /> {[p.address, p.city, p.state, p.country].filter(Boolean).join(", ") || "—"}
          </p>
          <div className="text-3xl font-display font-medium mb-4" style={{ color: "var(--wofy-primary)" }} data-testid="property-price">
            {fmtPrice(p.price, p.currency, p.price_period)}
          </div>

          <div className="flex flex-wrap gap-3 text-sm mb-6">
            {p.features?.bedrooms  != null && <span className="wofy-card !py-1 !px-3 inline-flex items-center gap-1.5"><Bed className="w-4 h-4" /> {p.features.bedrooms} bed</span>}
            {p.features?.bathrooms != null && <span className="wofy-card !py-1 !px-3 inline-flex items-center gap-1.5"><Bath className="w-4 h-4" /> {p.features.bathrooms} bath</span>}
            {p.features?.parking   != null && <span className="wofy-card !py-1 !px-3 inline-flex items-center gap-1.5"><Car className="w-4 h-4" /> {p.features.parking} parking</span>}
            {p.size_value && <span className="wofy-card !py-1 !px-3 inline-flex items-center gap-1.5"><Ruler className="w-4 h-4" /> {p.size_value} {p.size_unit}</span>}
          </div>

          <section className="wofy-card !p-4 mb-4" data-testid="property-description">
            <div className="overline mb-2">DESCRIPTION</div>
            <p className="text-sm whitespace-pre-line leading-relaxed">{p.description}</p>
          </section>

          {(p.features?.garden || p.features?.pool || p.features?.balcony || p.features?.furnished || p.features?.ac) && (
            <section className="wofy-card !p-4 mb-4">
              <div className="overline mb-2">FEATURES</div>
              <div className="flex flex-wrap gap-2 text-sm">
                {p.features.garden    && <span className="wofy-card !py-1 !px-3">🌳 Garden</span>}
                {p.features.pool      && <span className="wofy-card !py-1 !px-3">🏊 Pool</span>}
                {p.features.balcony   && <span className="wofy-card !py-1 !px-3">🌇 Balcony</span>}
                {p.features.furnished && <span className="wofy-card !py-1 !px-3">🛋️ Furnished</span>}
                {p.features.ac        && <span className="wofy-card !py-1 !px-3">❄️ Air conditioning</span>}
              </div>
            </section>
          )}
        </main>

        <aside className="wofy-card !p-4 h-fit sticky top-4" data-testid="property-sidebar">
          <div className="overline mb-2">CONTACT</div>
          <div className="text-sm mb-3">
            {p.business?.name ? (
              <Link to={`/business/${p.business.slug}`} className="inline-flex items-center gap-1.5 hover:underline">
                <Building2 className="w-4 h-4" /> {p.business.name}
              </Link>
            ) : (
              <span className="opacity-80">Posted by {p.posted_by_name || "Homeowner"}</span>
            )}
          </div>
          {p.contact_via === "email" && p.contact_value && (
            <a href={`mailto:${p.contact_value}`} className="text-sm inline-flex items-center gap-1.5 wofy-btn-primary w-full justify-center mb-2" data-testid="property-contact-email"><Mail className="w-4 h-4" /> Email</a>
          )}
          {p.contact_via === "phone" && p.contact_value && (
            <a href={`tel:${p.contact_value}`}   className="text-sm inline-flex items-center gap-1.5 wofy-btn-primary w-full justify-center mb-2" data-testid="property-contact-phone"><Phone className="w-4 h-4" /> Call {p.contact_value}</a>
          )}
          {p.contact_via === "socialpulse" && (
            <button onClick={() => toast.info("Social Pulse messaging coming soon.")} className="text-sm wofy-btn-primary w-full justify-center inline-flex items-center gap-1.5">💬 Send message</button>
          )}
          <div className="flex gap-2 mt-2">
            <button onClick={toggleFav} disabled={favBusy} className="wofy-btn-ghost flex-1 inline-flex items-center justify-center gap-1.5" data-testid="property-favorite">
              {favorited ? <><HeartOff className="w-4 h-4" /> Saved</> : <><Heart className="w-4 h-4" /> Save</>}
            </button>
            <button onClick={share} className="wofy-btn-ghost inline-flex items-center gap-1.5" data-testid="property-share"><Share2 className="w-4 h-4" /></button>
          </div>
          {!isOwner && user && (
            <div className="mt-3 text-center">
              <ReportButton kind="property" targetId={p.id} label="Report listing" />
            </div>
          )}
          {isOwner && (
            <div className="text-[11px] opacity-60 mt-3">You posted this · {p.view_count || 0} view{p.view_count === 1 ? "" : "s"} · {p.favorite_count || 0} favorite{p.favorite_count === 1 ? "" : "s"}</div>
          )}
        </aside>
      </div>
    </div>
  );
}
